import { useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { MIN_DURATION, EMPLOYEE_ROLES } from "../components/appointment-form/appointment-form.constants.js";
import { usePrevious } from "../helpers/common.js";
import "../appointment-details.types.js";
import "date-fns";
import "date-fns/locale";
const schema = (isNewPatient, isOpenAppointment) => Yup.object().shape({
  treatmentReferenceId: Yup.string().required(),
  overbooked: Yup.boolean(),
  employeeReferenceId: Yup.string().nullable(),
  comment: Yup.string(),
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  duration: Yup.number().when({
    is: (time) => time === null,
    then: () => Yup.number().nullable(),
    otherwise: () => Yup.number().min(MIN_DURATION).required()
  }),
  ...!isOpenAppointment && {
    startTime: Yup.string().nullable().required()
  },
  title: Yup.string().required(),
  openAppointmentReferenceId: Yup.string(),
  openedForOnlineBooking: Yup.boolean().nullable(),
  randomPractitioner: Yup.string().nullable(),
  employeeByRole: Yup.object().shape(
    EMPLOYEE_ROLES.reduce(
      (acc, role) => ({
        ...acc,
        [role]: Yup.array().of(Yup.string())
      }),
      {}
    )
  ),
  ...!isNewPatient && {
    patientReferenceId: Yup.string().required()
  },
  ...isNewPatient && {
    patient: Yup.object().shape({
      firstName: Yup.string().required(),
      lastName: Yup.string().required(),
      phoneNumber: Yup.string().required(),
      dateOfBirth: Yup.date().required(),
      email: Yup.string()
    })
  }
});
const useAppointmentForm = ({
  isNewPatient = false,
  initialValues,
  date,
  isOpenAppointment = false,
  appointmentReferenceId
} = {}) => {
  const previousAppointmentId = usePrevious(initialValues == null ? void 0 : initialValues.referenceId);
  const previousNewPatient = usePrevious(isNewPatient);
  const previousAppointmentReferenceId = usePrevious(appointmentReferenceId);
  const defaultValues = useMemo(
    () => initialValues ?? {
      duration: MIN_DURATION,
      patientReferenceId: "",
      practitionerReferenceId: "",
      title: "",
      treatmentReferenceId: "",
      details: "",
      comment: "",
      employeeReferenceId: "",
      randomPractitioner: null,
      startTime: date ?? /* @__PURE__ */ new Date(),
      openedForOnlineBooking: false
    },
    [initialValues]
  );
  const form = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues,
    resolver: yupResolver(schema(isNewPatient, isOpenAppointment))
  });
  useEffect(() => {
    if ((initialValues == null ? void 0 : initialValues.referenceId) && previousAppointmentId !== (initialValues == null ? void 0 : initialValues.referenceId) || previousAppointmentReferenceId !== appointmentReferenceId) {
      form.reset(initialValues, { keepDefaultValues: false, keepValues: false, keepDirty: false });
      form.trigger();
    }
  }, [initialValues]);
  useEffect(() => {
    if (isNewPatient !== previousNewPatient)
      form.trigger();
  }, [isNewPatient]);
  return {
    form
  };
};
export {
  useAppointmentForm
};
